import { z } from 'zod';

import { stripOfferMarkers } from '../offer-marker';
import {
  offerStandardPageResult,
  offerStandardPageSamePageResult,
  offerStandardPageUnavailableResult,
} from './tool-result-copy';
import type { ClientToolDefinition } from './types';

// `pageOptionId` is optional even though the server enums it: a call without
// one keeps the pre-same-page behaviour instead of failing to parse, and the
// comparison below must never match `undefined === undefined` on a page with no
// catalog id.
const schema = z
  .object({ narration: z.string(), pageOptionId: z.string().optional() })
  .passthrough();

export const offerStandardPageTool: ClientToolDefinition<typeof schema> = {
  schema,
  // Param-narration offer — see navigate's narrationPolicy note.
  narrationPolicy: 'drop',
  run({ narration, pageOptionId }, { deps }) {
    // Already on the offered page: keep the figure, drop the self-link.
    if (
      pageOptionId !== undefined &&
      deps.getCurrentPageOptionId() === pageOptionId
    ) {
      deps.appendAssistantText(stripOfferMarkers(narration, 'page'));
      return offerStandardPageSamePageResult;
    }
    // The single-value figure AND the page offer both live in the validated
    // narration. Render it as the assistant text so its [phrase](offer:page)
    // marker becomes an inline trigger, stamped with the id of the single-
    // value answer chart it describes (the drawer wires the click to that
    // chart's standard page + filters). The stamp source is that specific
    // chart, not "the latest chart", so an ordinary chart rendered later in
    // the turn can't retarget the offer.
    const chart = deps.getLastStandardPageAnswerChart();
    // No answer chart (a read renders none) or a page other than the chart's:
    // the link would be dead or open the wrong page, so nothing is shown and
    // the model answers again without one.
    if (
      !chart ||
      (pageOptionId !== undefined && pageOptionId !== chart.pageOptionId)
    ) {
      return offerStandardPageUnavailableResult;
    }
    deps.appendAssistantText(narration, { page: chart.id });
    return offerStandardPageResult;
  },
};
